import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameStore'
import { COURT_UPGRADES, CourtUpgrade } from '@/config/upgradesConfig'
import { courtUpgradeDays } from '@/config/constructionConfig'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { FACILITY_VISUALS, getFacilityAsset } from '@/config/campusConfig'
import { SectionDeck, IconTile, EmptyStateRich } from '../components/CourtVision'
import { ModifierPills } from '../components/campus/ModifierPills'
import {
  LandPlot, Warehouse, BadgeCheck, ArrowUpCircle, MousePointerClick, Sparkles,
  Lightbulb, Layers, Snowflake, Volume2, Frame, Umbrella, CircleDot, HardHat,
  type LucideIcon,
} from 'lucide-react'
import type { Court } from '@/store/gameStore'

const UPGRADE_ICONS: Record<string, LucideIcon> = {
  led_lighting: Lightbulb,
  premium_surface: Layers,
  climate_control: Snowflake,
  sound_system: Volume2,
  glass_walls: Frame,
  retractable_roof: Umbrella,
}

const COURT_VISUAL = FACILITY_VISUALS.find(v => v.id === 'court')

/** Pick the best illustration tier for a court given its type + upgrades */
function courtTier(court: Court, upgrades: string[]): number {
  if (court.type === 'indoor') return 3
  if (upgrades.includes('premium_surface')) return 2
  if (upgrades.includes('led_lighting')) return 1
  return 0
}

function courtImage(court: Court, upgrades: string[]): string | undefined {
  return COURT_VISUAL ? getFacilityAsset(COURT_VISUAL, courtTier(court, upgrades))?.image : undefined
}

export function UpgradesTabContent() {
  const { courts, money, courtUpgrades, queueConstruction, updateCourt, constructionQueue } = useGameStore()
  const [selectedCourtId, setSelectedCourtId] = useState<string | null>(null)

  // Listen for court selection from the game canvas
  useEffect(() => {
    const handleCourtSelected = (data: { id: string }) => {
      setSelectedCourtId(data.id)
    }
    EventBus.on(GameEvents.COURT_SELECTED, handleCourtSelected)
    return () => { EventBus.off(GameEvents.COURT_SELECTED, handleCourtSelected) }
  }, [])

  const selectedCourt = courts.find(c => c.id === selectedCourtId)
  const upgrades = selectedCourtId ? (courtUpgrades[selectedCourtId] || []) : []
  const selectedIndex = courts.findIndex(c => c.id === selectedCourtId)
  // A court is closed while its upgrade works are in progress
  const courtInWorks = !!selectedCourtId &&
    constructionQueue.some(j => j.kind === 'court_upgrade' && j.courtId === selectedCourtId)

  const getAvailableUpgrades = () => {
    if (!selectedCourt) return []
    return COURT_UPGRADES.filter(u =>
      u.applicableTo.includes(selectedCourt.type) &&
      !upgrades.includes(u.id)
    )
  }

  const getAppliedUpgrades = () => COURT_UPGRADES.filter(u => upgrades.includes(u.id))

  const handleApplyUpgrade = (upgrade: CourtUpgrade) => {
    if (!selectedCourtId || money < upgrade.cost || upgrades.includes(upgrade.id) || courtInWorks) return
    const days = courtUpgradeDays()
    // The works go through the timed construction pipeline; the upgrade is
    // applied when they finish (see completeConstruction). Until then the
    // court is closed and earns nothing.
    const ok = queueConstruction({
      kind: 'court_upgrade',
      label: `Pista ${selectedIndex + 1}: ${upgrade.name}`,
      totalDays: days,
      visualId: 'court',
      tier: 0,
      courtId: selectedCourtId,
      courtUpgradeId: upgrade.id,
      cost: upgrade.cost,
    })
    if (ok) {
      updateCourt(selectedCourtId, { isOperational: false })
      EventBus.emit(GameEvents.NOTIFY, {
        message: `Mejora iniciada: ${upgrade.name} · ${days} días (la pista queda cerrada durante la obra)`,
        type: 'success',
      })
    }
  }

  const totalUpgrades = Object.values(courtUpgrades).flat().length

  if (courts.length === 0) {
    return (
      <EmptyStateRich
        icon={ArrowUpCircle}
        title="Nada que mejorar todavía"
        sub="Construye tu primera pista y vuelve aquí para equiparla con LED, cristal y más"
      />
    )
  }

  return (
    <div>
      <SectionDeck
        title="Selecciona pista"
        icon={MousePointerClick}
        meta={`${totalUpgrades} mejora${totalUpgrades === 1 ? '' : 's'} en total`}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {courts.map((court, i) => {
            const courtUps = courtUpgrades[court.id] || []
            const isSelected = selectedCourtId === court.id
            const CourtIcon = court.type === 'outdoor' ? LandPlot : Warehouse
            const img = courtImage(court, courtUps)
            return (
              <button
                key={court.id}
                onClick={() => setSelectedCourtId(court.id)}
                className="upgrade-court-chip"
                style={{
                  border: isSelected ? '2px solid var(--gold)' : '2px solid transparent',
                  boxShadow: isSelected ? '0 0 0 3px var(--gold-fill)' : 'none',
                }}
              >
                <span
                  className="upgrade-court-thumb"
                  style={{ backgroundImage: img ? `url(${img})` : undefined }}
                />
                <span className="upgrade-court-chip-body">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}>
                    <CourtIcon size={13} style={{ color: isSelected ? 'var(--gold)' : 'var(--text-muted)' }} />
                    Pista {i + 1}
                  </span>
                  <span className="meta-text">
                    {court.type === 'indoor' ? 'Indoor' : 'Outdoor'}
                    {courtUps.length > 0 && ` · ${courtUps.length} mejora${courtUps.length === 1 ? '' : 's'}`}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </SectionDeck>

      {selectedCourt && (
        <>
          {/* Selected court hero: the upgrades visibly change its look */}
          <div className="upgrade-hero">
            <div
              className="upgrade-hero-img tx-glass-sheen"
              style={{ backgroundImage: (() => { const img = courtImage(selectedCourt, upgrades); return img ? `url(${img})` : undefined })() }}
            />
            <div className="upgrade-hero-info">
              <div className="card-title">Pista {selectedIndex + 1}</div>
              <div className="meta-text">
                {getAppliedUpgrades().length} de {getAppliedUpgrades().length + getAvailableUpgrades().length} mejoras instaladas
              </div>
            </div>
          </div>

          {courtInWorks && (
            <div className="hint-box" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <HardHat size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '2px' }} />
              <span>
                Mejora en curso · la pista está <strong>cerrada</strong> y no genera ingresos
                hasta que terminen las obras. Verás el temporizador en la barra superior.
              </span>
            </div>
          )}

          {getAppliedUpgrades().length > 0 && (
            <SectionDeck title="Instaladas" icon={BadgeCheck} meta={`${getAppliedUpgrades().length}`}>
              <div className="space-y-2">
                {getAppliedUpgrades().map(upgrade => {
                  const UpgradeIcon = UPGRADE_ICONS[upgrade.id] ?? CircleDot
                  return (
                    <div key={upgrade.id} className="row-tile">
                      <IconTile icon={UpgradeIcon} tone="success" />
                      <div className="row-tile-main">
                        <div className="row-tile-title">{upgrade.name}</div>
                        <div className="row-tile-sub">{upgrade.description}</div>
                      </div>
                      <span className="pill pill-success"><BadgeCheck size={10} /> Instalada</span>
                    </div>
                  )
                })}
              </div>
            </SectionDeck>
          )}

          <SectionDeck title="Disponibles" icon={ArrowUpCircle}>
            {getAvailableUpgrades().length === 0 ? (
              <EmptyStateRich
                icon={Sparkles}
                title="Pista a pleno rendimiento"
                sub="Ya has instalado todas las mejoras compatibles con esta pista"
              />
            ) : (
              <div className="space-y-2">
                {getAvailableUpgrades().map(upgrade => {
                  const canAfford = money >= upgrade.cost
                  const UpgradeIcon = UPGRADE_ICONS[upgrade.id] ?? CircleDot
                  return (
                    <button
                      key={upgrade.id}
                      className="row-tile"
                      onClick={() => handleApplyUpgrade(upgrade)}
                      disabled={!canAfford || courtInWorks}
                    >
                      <IconTile icon={UpgradeIcon} tone="cta" />
                      <div className="row-tile-main">
                        <div className="row-tile-title">{upgrade.name}</div>
                        <div className="row-tile-sub">{upgrade.description}</div>
                        <ModifierPills mods={upgrade.modifiers} style={{ marginTop: '4px' }} />
                      </div>
                      <div className="row-tile-side">
                        <span className={canAfford ? 'money-positive' : 'money-negative'} style={{ fontWeight: 700 }}>
                          {upgrade.cost.toLocaleString('es-ES')}€
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </SectionDeck>
        </>
      )}

      {!selectedCourt && (
        <EmptyStateRich
          icon={MousePointerClick}
          title="Elige una pista para empezar"
          sub="Cada pista tiene su propio catálogo de mejoras según sea outdoor o indoor"
        />
      )}
    </div>
  )
}
